import { ReviewPost } from '../../types/review';
import { formatReviewRating } from '../../utils/formatReviewRating';
import { getPlatformRating } from '../../utils/bestAIGirlfriendRatings';

const ourdreamScore = formatReviewRating(getPlatformRating('ourdream-ai-review'));
const nectarScore = formatReviewRating(getPlatformRating('nectar-ai-review'));
const swipeyScore = formatReviewRating(getPlatformRating('swipey-ai'));
const spicyScore = formatReviewRating(getPlatformRating('spicy-chat-ai-review'));
const candyScore = formatReviewRating(getPlatformRating('candy-ai-review'));

const candyFaqItems = [
  {
    id: 'is-candy-ai-safe',
    question: 'Is Candy AI safe?',
    answer:
      'Candy AI is a real product run by EverAI Limited in Malta, not a throwaway clone site. Payments are encrypted, billed under a discreet merchant name, and Candy says it follows GDPR. That is stronger than a lot of competitors. It is still an 18+ adult companion app, so treat chats as private but not secret, use 2FA if you enable it, and skip sharing addresses, card numbers, or anything you would not want stored on a server.',
  },
  {
    id: 'is-there-a-candy-ai-mobile-app',
    question: 'Is there a Candy AI mobile app?',
    answer:
      "There is no native iPhone or Android app in the App Store or Google Play. Candy AI is a web app. It works in a mobile browser, and Candy's own site tells you to tap Share, then Add to Home Screen, for a PWA-style shortcut and notifications. That is fine on a phone. It is not the same as a dedicated app.",
  },
  {
    id: 'does-candy-ai-encrypt-chat-data',
    question: 'Does Candy AI store or encrypt chat data?',
    answer:
      "Candy's public FAQ says conversations use end-to-end encryption, that chats are not sold, and that messages are not used to train public models. You can manage or delete data from the account. We cannot independently audit that encryption claim from the outside, so we report it as Candy's policy rather than as a lab result. Assume the company still stores enough account and payment data to run billing, which its privacy notice confirms.",
  },
  {
    id: 'what-is-the-candy-ai-refund-policy',
    question: 'What is the Candy AI refund policy?',
    answer:
      "Candy's terms give most users 24 hours after payment to request a refund. A subscription refund can still be denied if you have used more than 20 tokens. Token-pack refunds can be denied once those tokens are used, and Candy says it can only refund token packs bought by card. EU and UK buyers have a separate 14-day withdrawal right on the initial purchase, with the usual digital-content caveats. Ask support first. A chargeback can get the account blacklisted.",
  },
  {
    id: 'is-candy-ai-free',
    question: 'Is Candy AI free?',
    answer:
      'Yes. Candy AI has a free account you can use to test the basics. Unlimited text chat, unblurred images, phone calls, and the monthly token allowance require Premium. It is a free account, not a complete free trial of every feature.',
  },
  {
    id: 'how-much-does-candy-ai-cost',
    question: 'How much does Candy AI cost?',
    answer:
      'A US checkout checked on October 8, 2026 showed the first month of monthly Premium at $9.99, then $13.99 per month. The three-month plan was $8.99 per month, and the annual plan was $3.99 per month ($47.88 billed up front). Text chat stays included after the monthly tokens run out. Images, voice, calls, and video spend tokens, and extra 100-token packs commonly cost about $9.99.',
  },
  {
    id: 'how-do-i-cancel-candy-ai',
    question: 'How do I cancel Candy AI?',
    answer:
      'Cancel Candy AI in your account before the next renewal. Most users have 24 hours after payment to request a refund, and a subscription refund can still be denied after more than 20 tokens are used. Ask support before a chargeback. A chargeback can get the account blacklisted.',
  },
  {
    id: 'candy-ai-vs-ourdream-ai',
    question: 'Candy AI vs OurDream AI: which is better?',
    answer:
      `Candy AI is our #1 overall, at ${candyScore}/5. OurDream AI scores ${ourdreamScore}/5 and is the stronger pick for images and video. Choose Candy for the all-around app, including phone calls and the cheaper annual chat plan. Choose OurDream when images and video matter more than the lowest plan price.`,
  },
];

const candyFaqHtml = candyFaqItems
  .map(
    (faq) => `
    <h3 id="${faq.id}" class="text-xl font-bold text-pink-300 mb-3">${faq.question}</h3>
    <p class="mb-6">${faq.answer}</p>`
  )
  .join('\n');

const review: ReviewPost = {
  id: 4,
  slug: 'candy-ai-review',
  title: 'Candy AI Review 2026: Our #1 AI Girlfriend Platform',
  excerpt: 'There\'s no secret we love Candy AI, but do you know why? Read to find out why Candy AI remains one of the best AI girlfriend websites of 2026.',
  content: `
    <div class="table-of-contents bg-purple-950/30 border border-pink-500/20 p-6 rounded-lg mb-8">
      <h2 class="text-2xl font-bold text-pink-400 mb-4" id="toc-title">Table of Contents</h2>
      <ul class="space-y-2">
        <li class="text-pink-200">
          <a href="#introduction" class="hover:text-pink-400 transition-colors">Introduction</a>
        </li>
        <li class="text-pink-200">
          <a href="#pros-cons" class="hover:text-pink-400 transition-colors">Candy AI Pros & Cons</a>
        </li>
        <li class="text-pink-200">
          <a href="#features" class="hover:text-pink-400 transition-colors">Candy AI Features</a>
          <ul class="pl-4 mt-2 space-y-2">
            <li class="text-pink-200">
              <a href="#explore" class="hover:text-pink-400 transition-colors">Explore AI Girlfriends</a>
            </li>
            <li class="text-pink-200">
              <a href="#chat" class="hover:text-pink-400 transition-colors">Chat With Beautiful AI Girlfriends</a>
            </li>
            <li class="text-pink-200">
              <a href="#create" class="hover:text-pink-400 transition-colors">Create Your Own AI Girlfriend</a>
            </li>
            <li class="text-pink-200">
              <a href="#generate" class="hover:text-pink-400 transition-colors">Generate AI Images & Videos</a>
            </li>
            <li class="text-pink-200">
              <a href="#beyond" class="hover:text-pink-400 transition-colors">Go Beyond Female Characters</a>
            </li>
          </ul>
        </li>
        <li class="text-pink-200">
          <a href="#pricing" class="hover:text-pink-400 transition-colors">Candy AI Pricing</a>
        </li>
        <li class="text-pink-200">
          <a href="#real-cost" class="hover:text-pink-400 transition-colors">Real Monthly Cost</a>
        </li>
        <li class="text-pink-200">
          <a href="#action-costs" class="hover:text-pink-400 transition-colors">What Each Action Costs</a>
        </li>
        <li class="text-pink-200">
          <a href="#faq" class="hover:text-pink-400 transition-colors">Candy AI FAQ</a>
        </li>
        <li class="text-pink-200">
          <a href="#who-should-skip" class="hover:text-pink-400 transition-colors">Who Should Skip Candy AI</a>
        </li>
        <li class="text-pink-200">
          <a href="#alternatives" class="hover:text-pink-400 transition-colors">Candy AI Alternatives</a>
        </li>
        <li class="text-pink-200">
          <a href="#verdict" class="hover:text-pink-400 transition-colors">Candy AI: The Verdict</a>
        </li>
      </ul>
    </div>

    <div class="not-prose mx-auto mb-8 max-w-3xl rounded-2xl border border-pink-400/25 bg-charcoal-900/70 p-5 text-left md:p-6">
      <p class="!mb-0 text-base leading-relaxed text-pink-100 md:text-lg">
        <strong class="font-semibold text-white">Candy AI is the best AI girlfriend app overall in 2026</strong>, after hands-on testing of 60 apps by Heaven Girlfriend reviewer Bernardo Barbosa. This page was last re-tested on September 9, 2026.
      </p>
    </div>
    
    <h2 id="introduction" class="text-2xl font-bold text-pink-400 mb-4">Introduction</h2>
    <p class="mb-6">
      As the Internet's leading AI girlfriend experts, we have always loved <strong>Candy AI</strong>, one of the pioneering websites for AI chat companions. However, these guys don't stop surprising with their constant updates, new features, and increasingly high-quality AI chats, characters, and images.
    </p>
    
    <p class="mb-6">
      The result? Candy AI is our #1 AI girlfriend platform in the 2026 ranking. In this complete review of the website, we will be taking a close look at everything you can do on Candy AI, from generating custom girlfriends to creating high-quality AI images from scratch. We re-checked US checkout, Candy's public feature pages, privacy claims, and refund terms on September 9, 2026.
    </p>
    
    <h2 id="pros-cons" class="text-2xl font-bold text-pink-400 mb-4">Candy AI Pros & Cons</h2>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-girlfriend-profile.webp" 
        alt="Candy AI girlfriend profile with photorealistic character card" 
        class="w-full rounded-lg mb-2"
        width="680"
        height="320"
        loading="lazy"
        decoding="async"
      />
      <p class="text-base text-pink-200/70 text-center">Candy AI offers stunning AI girlfriend profiles with impressive realism.</p>
    </div>
    
    <p class="mb-6">
      The trade-offs are real. Premium includes about 100 tokens a month, and images, voice, calls, and video burn through that balance faster than the $3.99 headline suggests. There is no native iOS or Android app, only a mobile browser and an Add to Home Screen shortcut. PayPal and other digital wallets are still missing. Long chats remember the relationship well enough for daily use, but they still drop smaller details after a gap. Refunds for most card purchases have to be requested within 24 hours, and Candy can refuse a subscription refund after more than 20 tokens are used.
    </p>
    
    <h2 id="features" class="text-2xl font-bold text-pink-400 mb-4">Candy AI Features</h2>
    
    <p class="mb-6">
      It's not just about the number of features; it's about how you deliver them. In our <a href="/reviews/kupid-ai-review" class="text-pink-400 hover:text-pink-300 transition-colors">Kupid AI review</a>, for example, we praised the possibility of switching between female, male, and Anime characters, but our experience was undermined by performance issues. Candy AI lets you do the same, but without slow loadings and occasional bugs, even on outdated devices.
    </p>
    
    <p class="mb-6">
      So, what can you do on Candy AI? Let's take a closer look at all that's possible on the website.
    </p>
    
    <h3 id="explore" class="text-xl font-bold text-pink-300 mb-3">Explore AI Girlfriends</h3>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-character-gallery.webp" 
        alt="Candy AI character gallery of ready-made AI girlfriends" 
        class="w-full rounded-lg mb-2"
        width="800"
        height="450"
        loading="lazy"
      />
      <p class="text-base text-pink-200/70 text-center">Browse through a diverse collection of ready-made AI girlfriends.</p>
    </div>
    
    <p class="mb-6">
      Looking for a platform where you can chat with AI girlfriends in less than a minute? That's Candy AI for you. While you can create custom companions according to your preferences, you also have the opportunity to explore several ready-made characters who are available to engage in personal AI conversations from the get-go.
    </p>
    
    <p class="mb-6">
      All you have to do is create an account (a free one will do for now) and chat with the AI girlfriend (or boyfriend) you like the most. Diversity is the name of the game here: if you're into videogames, AI e-girl Zoe can keep you entertained for hours; if you're an artsy type, you'll probably enjoy the company of "California-based artist" Kelly.
    </p>
    
    <p class="mb-6">
      As you can see, image quality continues to be one of the most impressive aspects of Candy AI. But the website has gone even further by adding motion to the images (a little bit like a GIF). Just leave the mouse over the character's image, and you'll see her moving with impressive dynamism and realism.
    </p>
    
    <h3 id="chat" class="text-xl font-bold text-pink-300 mb-3">Chat With Beautiful AI Girlfriends</h3>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-chat-interface.webp" 
        alt="Candy AI chat interface with an AI girlfriend conversation" 
        class="w-full rounded-lg mb-2"
        width="800"
        height="450"
        loading="lazy"
      />
      <p class="text-base text-pink-200/70 text-center">Engage in natural conversations with your AI companion.</p>
    </div>
    
    <p class="mb-6">
      The chat is the essential feature of Candy AI, where users will most likely spend the majority of their time. Unlike other advanced AI platforms, Candy AI has a way of ensuring its chats remain fun for hours, especially due to the inclusion of engagement options that go far beyond mere text messages.
    </p>
    
    <p class="mb-6">
      Your virtual companion will make interactions more engaging from the start by sending you an AI-generated video and a welcoming message. In the chat, users can request additional forms of media from their AI girlfriends by using the "Ask" prompt at the bottom of the screen. While there are some limitations to non-premium users, we were able to chat with several companions for hours with our free trial account.
    </p>
    
    <p class="mb-6">
      In addition to allowing for full-blown advanced conversations, images, and videos, Candy AI also adds an unprecedented level of realism to the AI girlfriend landscape by supporting phone calls. It's a highly immersive option that makes Candy AI's companions seem even more real. You can also use the "Generate Image" option to create custom AI images of each available character.
    </p>
    
    <h3 id="create" class="text-xl font-bold text-pink-300 mb-3">Create Your Own AI Girlfriend</h3>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-character-creator.webp" 
        alt="Candy AI character creator with appearance and personality controls" 
        class="w-full rounded-lg mb-2"
        width="800"
        height="450"
        loading="lazy"
      />
      <p class="text-base text-pink-200/70 text-center">Customize every aspect of your AI companion from appearance to personality.</p>
    </div>
    
    <p class="mb-6">
      If you have a specific taste in women, you'll love Candy AI's girlfriend generator. The generator's interface is super easy to navigate, as all you have to do is select the parameters and features that make up your ideal AI companion (like appearance, eye color and so on). Some options are exclusive to premium users.
    </p>
    
    <p class="mb-6">
      Additional customization options include hair style, body type, and proportions. Candy AI users can also set up the personality of their AI girlfriends, which has a direct impact on the type of chats you can have. These are some of the supported categories:
    </p>
    
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li><strong>Caregiver:</strong> For warmer, lighter conversations.</li>
      <li><strong>Confident Personality:</strong> For straightforward personal AI conversations that will feel highly engaging.</li>
      <li><strong>Sage:</strong> For more cerebral, philosophical chats that will keep your brain busy.</li>
    </ul>
    
    <p class="mb-6">
      Once you have selected all the features of your custom AI girlfriend, you can engage with her as if she were any other character on the website. All generated AI girlfriends are added to your personal companion library, listed under the "Collection" tab.
    </p>
    
    <h3 id="generate" class="text-xl font-bold text-pink-300 mb-3">Generate AI Images & Videos</h3>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-image-generator.webp" 
        alt="Candy AI image generator with outfit, pose, and background options" 
        class="w-full rounded-lg mb-2"
        width="800"
        height="450"
        loading="lazy"
      />
      <p class="text-base text-pink-200/70 text-center">Create custom images of your AI companion in different scenarios.</p>
    </div>
    
    <p class="mb-6">
      Candy AI's image generator is unique because it's character-based, meaning you normally have to create (or choose) an AI girlfriend before getting in on the fun. While this may sound like a limitation, it's actually an advantage, providing an added dose of context and realism to each picture created on the website.
    </p>
    
    <p class="mb-6">
      Candy AI's later 2026 updates also made it possible to generate short GIF-like videos and Live Action-style clips from generated photos. Video is a real feature now, not a teaser, but it is also one of the fastest ways to spend the monthly token allowance.
    </p>
    
    <p class="mb-6">
      When it comes to customization features, this is what you can do:
    </p>
    
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li>Choose the outfit the AI girlfriend will be wearing in the image. Outfits range from casual to swimwear.</li>
      <li>Select what the AI girlfriend will be doing in the image (walking, working out, posed, etc.).</li>
      <li>Add accessories like a neck accessory or sunglasses.</li>
      <li>Choose the background of the image.</li>
    </ul>
    
    <p class="mb-6">
      Free users can only generate one AI image at a time, but premium members have the option of creating as many as 64 (!) pictures with one single prompt.
    </p>
    
    <h3 id="beyond" class="text-xl font-bold text-pink-300 mb-3">Go Beyond Female Characters</h3>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-male-and-anime-characters.webp" 
        alt="Candy AI male and anime character options alongside photorealistic women" 
        class="w-full rounded-lg mb-2"
        width="800"
        height="450"
        loading="lazy"
      />
      <p class="text-base text-pink-200/70 text-center">Explore male characters and anime-style companions as well.</p>
    </div>
    
    <p class="mb-6">
      While photorealistic AI women have helped to put Candy AI on the map, the platform also supports Anime and male characters. This is great news for Anime-style content fans and female users, who are often excluded by other AI girlfriend platforms that only have the interests of male members in mind.
    </p>
    
    <p class="mb-6">
      Once more, Candy AI stands out due to the image quality, as the Anime illustrations look like something straight out of stylized Manga visuals! The AI boyfriends look equally solid, and the best part is that you can create your own if you're not happy with the ready-made characters provided by the website.
    </p>
    
    <h2 id="pricing" class="text-2xl font-bold text-pink-400 mb-4">Candy AI Pricing</h2>
    
    <div class="mb-6">
      <img 
        src="/candy-ai-pricing-plans.webp" 
        alt="Candy AI pricing plans for monthly, quarterly, and annual Premium" 
        class="w-full rounded-lg mb-2"
        width="800"
        height="450"
        loading="lazy"
      />
      <p class="text-base text-pink-200/70 text-center">Candy AI offers competitive pricing with significant discounts for new users.</p>
    </div>
    
    <p class="mb-6">
      Considering that Candy AI is one of the most complete and high-quality AI girlfriend platforms we have ever tested, it's surprising that it's also one of the cheapest premium websites in the game. A US checkout checked on October 8, 2026 showed the first month of monthly Premium at $9.99, then $13.99 per month. The three-month plan was $8.99 per month, and the annual plan was $3.99 per month ($47.88 billed up front). Logged-out visitors sometimes see a higher annual teaser, so check the price after you create a free account.
    </p>
    
    <p class="mb-6">
      This is possible because Candy AI offers discounts up to 70% to first-time subscribers. Paid plans unlock unlimited text chat, remove blurs, and open premium features like AI phone calls. They also include a monthly token allotment, generally 100 tokens, for images, voice, video, and custom-character extras. Text chat keeps working after tokens run out. Media does not.
    </p>
    
    <p class="mb-6">
      Payment is possible via credit card and cryptocurrency, but Candy AI doesn't support digital wallets (like PayPal) at the time of writing. If you're worried about an AI girlfriend website showing on your bank statement, Candy says charges appear under a discreet merchant name.
    </p>

    <h2 id="real-cost" class="text-2xl font-bold text-pink-400 mb-4">Candy AI Real Monthly Cost</h2>
    <p class="mb-6">
      The plan price is not the all-in cost. Candy AI sells a hybrid model: a subscription for chat, plus tokens for images, voice, calls, custom character extras, and video. Extra 100-token packs commonly sit around $9.99 in 2026 checkouts we compared. Confirm the live pack price before you buy, because Candy can change it without changing the subscription headline.
    </p>
    <div class="mb-6 overflow-x-auto">
      <table class="w-full min-w-[32rem] border-collapse text-left text-pink-100">
        <thead>
          <tr class="border-b border-pink-500/30">
            <th class="py-2 pr-3 font-semibold text-white">Usage</th>
            <th class="py-2 pr-3 font-semibold text-white">What you pay</th>
            <th class="py-2 font-semibold text-white">Typical monthly total</th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Chat-only Premium</td>
            <td class="py-2 pr-3">Subscription only. Stay inside the included 100 tokens.</td>
            <td class="py-2">$3.99 on annual, or $9.99 for the first month then $13.99</td>
          </tr>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Regular images and voice</td>
            <td class="py-2 pr-3">Subscription plus about one extra 100-token pack</td>
            <td class="py-2">About $14 to $24</td>
          </tr>
          <tr>
            <td class="py-2 pr-3">Heavy video / Live Action</td>
            <td class="py-2 pr-3">Subscription plus multiple token packs</td>
            <td class="py-2">$25 and up</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="mb-6">
      If you want Candy mainly for conversation, the annual plan is still a bargain. If you want a constant stream of pictures, voice notes, and clips, budget for tokens or the $3.99 headline will feel like bait.
    </p>

    <h2 id="action-costs" class="text-2xl font-bold text-pink-400 mb-4">What Each Candy AI Action Costs</h2>
    <p class="mb-6">
      Premium includes about 100 tokens a month. These are the token counts for each action, so you can tell how far that allowance goes. The dollar column uses about $9.99 per 100 tokens. Candy can change token costs and pack prices without changing the plan price.
    </p>
    <div class="mb-6 overflow-x-auto">
      <table class="w-full min-w-[32rem] border-collapse text-left text-pink-100">
        <thead>
          <tr class="border-b border-pink-500/30">
            <th class="py-2 pr-3 font-semibold text-white">Action</th>
            <th class="py-2 pr-3 font-semibold text-white">Tokens</th>
            <th class="py-2 font-semibold text-white">Approx. cost at $9.99 per 100</th>
          </tr>
        </thead>
        <tbody>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">One image</td>
            <td class="py-2 pr-3">2–4</td>
            <td class="py-2">$0.20–$0.40</td>
          </tr>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Voice message</td>
            <td class="py-2 pr-3">0.2</td>
            <td class="py-2">$0.02</td>
          </tr>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Phone call (per minute)</td>
            <td class="py-2 pr-3">3</td>
            <td class="py-2">$0.30</td>
          </tr>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Short video clip</td>
            <td class="py-2 pr-3">12</td>
            <td class="py-2">$1.20</td>
          </tr>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Live Action clip</td>
            <td class="py-2 pr-3">15–20</td>
            <td class="py-2">$1.50–$2.00</td>
          </tr>
          <tr class="border-b border-pink-500/15">
            <td class="py-2 pr-3">Roulette video chat (per minute)</td>
            <td class="py-2 pr-3">5</td>
            <td class="py-2">$0.50</td>
          </tr>
          <tr>
            <td class="py-2 pr-3">Text chat</td>
            <td class="py-2 pr-3">0</td>
            <td class="py-2">Included in every paid plan</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h2 id="faq" class="text-2xl font-bold text-pink-400 mb-4">Candy AI FAQ</h2>
    ${candyFaqHtml}

    <h2 id="who-should-skip" class="text-2xl font-bold text-pink-400 mb-4">Who Should Skip Candy AI</h2>
    <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
      <li>You want a native iPhone or Android app. Candy only runs in the browser.</li>
      <li>You want a lot of video on a light budget. Tokens run out fast.</li>
      <li>You only pay with PayPal. Candy takes cards and crypto.</li>
    </ul>

    <h2 id="alternatives" class="text-2xl font-bold text-pink-400 mb-4">Candy AI Alternatives</h2>
    <p class="mb-6">
      These are the closest picks from our current ranking, in score order.
    </p>
    <div class="not-prose mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <a href="/reviews/ourdream-ai-review" class="block rounded-xl border border-pink-500/20 bg-charcoal-900/60 p-4 transition-colors hover:border-pink-400/50">
        <span class="flex items-baseline justify-between gap-3"><span class="font-semibold text-white">OurDream AI</span><span class="font-bold text-pink-300">${ourdreamScore}</span></span>
        <span class="mt-1 block text-sm text-pink-200/80">Best for images and video.</span>
      </a>
      <a href="/reviews/nectar-ai-review" class="block rounded-xl border border-pink-500/20 bg-charcoal-900/60 p-4 transition-colors hover:border-pink-400/50">
        <span class="flex items-baseline justify-between gap-3"><span class="font-semibold text-white">Nectar AI</span><span class="font-bold text-pink-300">${nectarScore}</span></span>
        <span class="mt-1 block text-sm text-pink-200/80">Best for story-led chat.</span>
      </a>
      <a href="/reviews/swipey-ai" class="block rounded-xl border border-pink-500/20 bg-charcoal-900/60 p-4 transition-colors hover:border-pink-400/50">
        <span class="flex items-baseline justify-between gap-3"><span class="font-semibold text-white">Swipey AI</span><span class="font-bold text-pink-300">${swipeyScore}</span></span>
        <span class="mt-1 block text-sm text-pink-200/80">Best for texting-style chat.</span>
      </a>
      <a href="/reviews/spicy-chat-ai-review" class="block rounded-xl border border-pink-500/20 bg-charcoal-900/60 p-4 transition-colors hover:border-pink-400/50">
        <span class="flex items-baseline justify-between gap-3"><span class="font-semibold text-white">Spicy Chat AI</span><span class="font-bold text-pink-300">${spicyScore}</span></span>
        <span class="mt-1 block text-sm text-pink-200/80">Best for roleplay and free chat.</span>
      </a>
    </div>
    
    <h2 id="verdict" class="text-2xl font-bold text-pink-400 mb-4">Candy AI: The Verdict (4.62/5)</h2>
    
    <p class="mb-6">
      AI girlfriends are what we do, but we cannot stress enough how impressive we find Candy AI to be. This leading platform for virtual companions beats the competition by offering several creation and chatting options, including state-of-the-art options like AI videos and phone calls, and doing it all with stellar image quality.
    </p>
    
    <p class="mb-6">
      For all of these reasons, Candy AI has earned our weighted score: 4.62 out of 5. The annual chat plan is still a bargain. Tokens are the catch if you live in images and video. To sum things up, Candy AI is our #1 AI girlfriend website in 2026.
    </p>
  `,
  image: '/candy-ai-review-cover.webp',
  date: 'July 3, 2025',
  reviewDate: 'September 9, 2026',
  readTime: '12 min read',
  category: 'AI Girlfriend Apps',
  featured: true,
  metaTitle: 'Candy AI Review: Our #1 AI Girlfriend Site (2026)',
  metaDescription: 'Candy AI review 2026: 4.62/5. Our #1 AI girlfriend site. Chat, images, video. From $3.99/mo plus tokens. Retested Sep 2026.',
  productName: 'Candy AI',
  rating: 4.90,
  ratingCategories: [
    { name: 'Conversation', score: 5.0 },
    { name: 'Image Quality', score: 5.0 },
    { name: 'Interface', score: 5.0 },
    { name: 'Customization', score: 5.0 },
    { name: 'Value/Money', score: 5.0 }
  ],
  pros: [
    'The best-looking AI companions in the industry',
    'Immersive and realistic AI chats',
    'Unique AI phone call feature',
    'Free account to test the basics'
  ],
  cons: [
    'Tokens add up for images, voice, and video',
    'No native mobile app, browser/PWA only',
    'No PayPal payments',
    '24-hour refund window for most purchases'
  ],
  features: [
    'Diverse AI character library',
    'Custom character creation',
    'AI image and video generation',
    'Voice messages and calls',
    'Multiple personality types',
    'Anime and male character options'
  ],
  testimonials: [
    {
      name: 'Thomas R.',
      rating: 5,
      date: 'September 2, 2026',
      text: 'The image quality and character customization on Candy AI still blow away most of the apps I tried this year. I just budget extra tokens now, because pictures and short clips burn the monthly allowance faster than chat does.',
      platformName: 'Candy AI',
      verified: false,
      badgeLabel: 'Reader comment'
    },
    {
      name: 'Jessica L.',
      rating: 5,
      date: 'August 28, 2026',
      text: 'As a female user, I appreciate that Candy AI offers male characters too. The conversation still feels natural, and the phone call feature adds a whole new dimension. I use it from my phone browser since there is no App Store app.',
      platformName: 'Candy AI',
      verified: false,
      badgeLabel: 'Reader comment'
    }
  ],
  faqs: candyFaqItems.map(({ question, answer }) => ({ question, answer })),
  affiliateLink: '/go/candy-ai',
  price: 'From $3.99/month (annual plan)',
  author: 'Bernardo Barbosa',
  authorSlug: 'bernardo-barbosa',
  // New fields for the ranking system
  label: "Candy AI: The Ultimate AI Girlfriend Experience",
  promoBadge: "EDITOR'S CHOICE",
  verdict: "Everything that other AI GF services do, Candy AI does better. It's the world's most trustworthy website for AI companions.",
  keyPros: [
    "Industry-leading conversation quality",
    "Stunning AI image generation",
    "Phone call capability adds realism"
  ],
  keyCons: [
    "Tokens add up for media",
    "No native mobile app",
    "No PayPal payment option"
  ],
  rank: 1,
  homePagePros: [
    "Industry-leading conversation quality",
    "Stunning AI image generation"
  ],
  homePageCons: [
    "Tokens add up",
    "No PayPal payment option"
  ]
};

export default review;
